import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

type Props = { images: string[]; name: string; initialIndex?: number; onClose: () => void };
type Point = { x: number; y: number };
type View = Point & { scale: number };
const originalView: View = { scale: 1, x: 0, y: 0 };

function measureGesture(points: Point[]) {
  const [first, second = first] = points;
  return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2,
    distance: Math.hypot(second.x - first.x, second.y - first.y) };
}

export default function ProductImageViewer({ images, name, initialIndex = 0, onClose }: Props) {
  const [index, setIndex] = useState(Math.min(initialIndex, images.length - 1));
  const [view, setView] = useState(originalView);
  const panel = useRef<HTMLDivElement>(null);
  const area = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const pointers = useRef(new Map<number, Point>());
  const dragged = useRef(false);
  const close = useRef(onClose);
  close.current = onClose;
  const fitView = useCallback((next: View): View => {
    const scale = Math.max(1, Math.min(4, next.scale));
    const width = area.current?.clientWidth ?? 0, height = area.current?.clientHeight ?? 0;
    const photo = image.current;
    const fit = photo?.naturalWidth && photo.naturalHeight ? Math.min(width / photo.naturalWidth, height / photo.naturalHeight) : 1;
    const maxX = Math.max(0, ((photo?.naturalWidth ? photo.naturalWidth * fit : width) * scale - width) / 2);
    const maxY = Math.max(0, ((photo?.naturalHeight ? photo.naturalHeight * fit : height) * scale - height) / 2);
    return { scale, x: Math.max(-maxX, Math.min(maxX, next.x)), y: Math.max(-maxY, Math.min(maxY, next.y)) };
  }, []);
  const resetView = useCallback(() => { setView(originalView); pointers.current.clear(); }, []);
  const move = (direction: number) => { setIndex(value => (value + direction + images.length) % images.length); resetView(); };
  const pointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    if (!pointers.current.size) dragged.current = false;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size > 1) dragged.current = true;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  };
  const pointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    event.stopPropagation();
    const previous = measureGesture(Array.from(pointers.current.values()));
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const next = measureGesture(Array.from(pointers.current.values()));
    if (next.x !== previous.x || next.y !== previous.y) dragged.current = true;
    const bounds = event.currentTarget.getBoundingClientRect();
    setView(value => {
      const scale = previous.distance > 0 ? Math.max(1, Math.min(4, value.scale * next.distance / previous.distance)) : value.scale;
      const ratio = scale / value.scale;
      return fitView({ scale,
        x: next.x - bounds.left - bounds.width / 2 - (previous.x - bounds.left - bounds.width / 2 - value.x) * ratio,
        y: next.y - bounds.top - bounds.height / 2 - (previous.y - bounds.top - bounds.height / 2 - value.y) * ratio });
    });
  };
  const pointerEnd = (event: PointerEvent<HTMLDivElement>) => { pointers.current.delete(event.pointerId); };
  useEffect(() => {
    setIndex(value => Math.max(0, Math.min(value, images.length - 1)));
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); close.current(); }
      if (event.key === 'ArrowRight') { event.preventDefault(); setIndex(value => (value + 1) % images.length); resetView(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); setIndex(value => (value - 1 + images.length) % images.length); resetView(); }
      if (['+', '=', '-'].includes(event.key)) { event.preventDefault(); setView(value => fitView({ ...value, scale: value.scale + (event.key === '-' ? -.5 : .5) })); }
      if (event.key === '0') { event.preventDefault(); resetView(); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(panel.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel.current)) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown, true);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', keydown, true); previous?.focus(); };
  }, [images.length, fitView, resetView]);

  if (!images.length) return null;
  return createPortal(<Overlay ref={panel} role="dialog" aria-modal="true" aria-label={`${name} 이미지 확대 보기`} tabIndex={-1} onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <Toolbar><span>{index + 1} / {images.length}</span>
      <button type="button" aria-label="이미지 확대 보기 닫기" onClick={onClose}>닫기</button></Toolbar>
    <ImageArea ref={area} $zoomed={view.scale > 1} onPointerDown={pointerDown} onPointerMove={pointerMove}
      onPointerUp={pointerEnd} onPointerCancel={pointerEnd} onLostPointerCapture={pointerEnd}
      onClick={event => { event.stopPropagation(); if (!dragged.current && event.target === event.currentTarget) onClose(); }}>
      <img ref={image} src={images[index]} alt={`${name} 이미지 ${index + 1}`} draggable={false}
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
        onDoubleClick={() => setView(value => value.scale > 1 ? originalView : { ...originalView, scale: 2 })} />
    </ImageArea>
    {images.length > 1 && <Navigation><button type="button" onClick={() => move(-1)}>이전 이미지</button><button type="button" onClick={() => move(1)}>다음 이미지</button></Navigation>}
  </Overlay>, document.body);
}
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 3000; background: rgba(0,0,0,.95); color: white;
  display: flex; flex-direction: column; padding: max(12px, env(safe-area-inset-top)) 12px max(12px, env(safe-area-inset-bottom)); box-sizing: border-box;
  button { min-width: 44px; min-height: 44px; padding: 8px 16px; border: 1px solid #777; border-radius: 8px; color: white; background: #222; font: inherit; cursor: pointer; }
  button:focus-visible { outline: 3px solid #bfa5ff; outline-offset: 2px; }
`;
const Toolbar = styled.div`display: flex; align-items: center; gap: 12px; span { flex: 1; }`;
const ImageArea = styled.div<{ $zoomed: boolean }>`
  flex: 1; min-height: 0; overflow: hidden; margin: 12px 0; touch-action: none; user-select: none; overscroll-behavior: contain;
  cursor: ${p => p.$zoomed ? 'grab' : 'default'};
  img { display: block; width: 100%; height: 100%; max-width: none; object-fit: contain; touch-action: none; }
`;
const Navigation = styled.div`display: flex; justify-content: center; gap: 16px;`;
